import React, { useState, useEffect } from 'react';
import { signInWithRedirect, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { toast } from 'react-toastify';
import styles from './Login.module.css';

// Helper to decode JWT
function decodeJwt(token) {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return { error: 'Invalid JWT', details: e.message };
  }
}

const Login = ({ onLoginSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  const [accessToken, setAccessToken] = useState(null);
  const [idToken, setIdToken] = useState(null);

  // Debug logging helper
  const logDebug = (stage, data) => {
    console.log(`[Auth Debug] ${stage}:`, data);
    setDebugInfo(prev => ({
      ...prev,
      lastStage: stage,
      [`${stage}Data`]: data,
      timestamp: new Date().toISOString()
    }));
  };

  // Check if user is returning from OAuth redirect
  useEffect(() => {
    const checkOAuthRedirect = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      const state = urlParams.get('state');
      
      logDebug('URL_CHECK', {
        fullUrl: window.location.href,
        hasCode: !!code,
        codeLength: code?.length,
        error,
        errorDescription,
        state,
        timestamp: new Date().toISOString()
      });
      
      if (error) {
        logDebug('OAUTH_ERROR', { error, errorDescription });
        toast.error(`Authentication error: ${errorDescription || error}`);
        return;
      }

      if (code) {
        setIsLoading(true);
        try {
          logDebug('PRE_SESSION_CHECK', {
            message: 'About to call fetchAuthSession',
            hasCode: true,
            codePrefix: code.substring(0, 8) + '...'
          });

          // Try to get session with force refresh first
          const session = await fetchAuthSession({ forceRefresh: true });
          setAccessToken(session?.tokens?.accessToken?.toString() || null);
          setIdToken(session?.tokens?.idToken?.toString() || null);
          logDebug('POST_SESSION_CHECK', {
            hasSession: !!session,
            hasTokens: !!session?.tokens,
            hasAccessToken: !!session?.tokens?.accessToken,
            hasIdToken: !!session?.tokens?.idToken,
            tokenExpiry: session?.tokens?.accessToken?.payload?.exp,
            sessionKeys: Object.keys(session || {}),
            tokenKeys: Object.keys(session?.tokens || {})
          });

          if (session?.tokens?.accessToken && session?.tokens?.idToken) {
            try {
              logDebug('FETCHING_USER', { message: 'Getting user details from ID token' });
              const currentUser = await getCurrentUser();
              const idPayload = decodeJwt(session.tokens.idToken.toString());

              logDebug('USER_DETAILS', {
                userId: currentUser.userId,
                username: currentUser.username,
                email: idPayload.email,
                sub: idPayload.sub,
                idPayload
              });

              // Successfully authenticated
              onLoginSuccess({
                id: idPayload.sub,
                cognitoId: currentUser.userId,
                username: currentUser.username,
                email: idPayload.email,
                name: idPayload.email?.split('@')[0] || currentUser.username,
                sub: idPayload.sub
              });

              // Clean up the URL but preserve the origin
              const cleanUrl = new URL(process.env.REACT_APP_REDIRECT_SIGN_IN || window.location.origin);
              window.history.replaceState({}, document.title, cleanUrl.origin);
              
              logDebug('AUTH_SUCCESS', {
                userId: currentUser.userId,
                redirectUrl: cleanUrl.origin
              });

              toast.success('Successfully signed in!');
            } catch (userError) {
              logDebug('USER_FETCH_ERROR', {
                error: userError.message,
                stack: userError.stack,
                sessionValid: !!session?.tokens?.accessToken
              });
              throw userError;
            }
          } else {
            logDebug('NO_TOKENS', {
              sessionExists: !!session,
              availableKeys: Object.keys(session || {}),
              tokenKeys: Object.keys(session?.tokens || {})
            });

            // Try one more time without force refresh
            logDebug('RETRY_SESSION', { message: 'Attempting to fetch session without force refresh' });
            const retrySession = await fetchAuthSession();
            setIdToken(retrySession?.tokens?.idToken?.toString() || null);
            logDebug('RETRY_RESULT', {
              hasSession: !!retrySession,
              hasTokens: !!retrySession?.tokens,
              hasAccessToken: !!retrySession?.tokens?.accessToken,
              hasIdToken: !!retrySession?.tokens?.idToken
            });

            if (!retrySession?.tokens?.accessToken) {
              toast.error('Failed to establish session after authentication');
            }
          }
        } catch (error) {
          logDebug('AUTH_ERROR', {
            error: error.message,
            stack: error.stack,
            type: error.constructor.name,
            code: error.code
          });
          toast.error(`Authentication failed: ${error.message}`);
        } finally {
          setIsLoading(false);
        }
      } else {
        // No code in URL, check for existing session
        try {
          logDebug('CHECKING_EXISTING_SESSION', { message: 'No code in URL, checking for existing session' });
          const session = await fetchAuthSession();
          setAccessToken(session?.tokens?.accessToken?.toString() || null);
          setIdToken(session?.tokens?.idToken?.toString() || null);
          logDebug('EXISTING_SESSION_RESULT', {
            hasSession: !!session,
            hasTokens: !!session?.tokens,
            hasAccessToken: !!session?.tokens?.accessToken,
            hasIdToken: !!session?.tokens?.idToken
          });

          if (session?.tokens?.accessToken && session?.tokens?.idToken) {
            const currentUser = await getCurrentUser();
            const idPayload = decodeJwt(session.tokens.idToken.toString());
            onLoginSuccess({
              id: idPayload.sub,
              cognitoId: currentUser.userId,
              username: currentUser.username,
              email: idPayload.email,
              name: idPayload.email?.split('@')[0] || currentUser.username,
              sub: idPayload.sub
            });
          }
        } catch (error) {
          logDebug('EXISTING_SESSION_ERROR', {
            error: error.message,
            type: error.constructor.name
          });
        }
      }
    };

    checkOAuthRedirect();
  }, [onLoginSuccess]);

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      
      // Check if environment variables are loaded
      const config = {
        userPoolId: process.env.REACT_APP_USER_POOL_ID,
        userPoolClientId: process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID,
        domain: process.env.REACT_APP_COGNITO_DOMAIN,
        redirectSignIn: [process.env.REACT_APP_REDIRECT_SIGN_IN],
        redirectSignOut: [process.env.REACT_APP_REDIRECT_SIGN_OUT]
      };
      
      console.log('Starting Google sign-in with config:', {
        hasUserPoolId: !!config.userPoolId,
        hasClientId: !!config.userPoolClientId,
        hasDomain: !!config.domain,
        hasRedirectSignIn: !!config.redirectSignIn?.[0],
        hasRedirectSignOut: !!config.redirectSignOut?.[0],
        redirectSignIn: config.redirectSignIn
      });
      
      if (!config.userPoolId || !config.userPoolClientId || !config.domain) {
        throw new Error('Missing required environment variables');
      }
      
      await signInWithRedirect({ 
        provider: 'Google',
        options: {
          redirectSignIn: config.redirectSignIn[0],
          redirectSignOut: config.redirectSignOut[0],
          scopes: ['openid', 'email']
        }
      });
    } catch (error) {
      console.error('Error signing in with Google:', error);
      setDebugInfo({
        error: error.message,
        stage: 'google_signin',
        config: {
          hasUserPoolId: !!process.env.REACT_APP_USER_POOL_ID,
          hasClientId: !!process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID,
          hasDomain: !!process.env.REACT_APP_COGNITO_DOMAIN,
          hasRedirectSignIn: !!process.env.REACT_APP_REDIRECT_SIGN_IN,
          hasRedirectSignOut: !!process.env.REACT_APP_REDIRECT_SIGN_OUT
        }
      });
      
      if (error.message.includes('oauth param not configured')) {
        toast.error('OAuth not configured properly. Please check Cognito settings.');
      } else if (error.message.includes('Missing required environment variables')) {
        toast.error('Configuration error: Missing environment variables');
      } else {
        toast.error(`Sign-in failed: ${error.message}`);
      }
      
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      setIsLoading(true);
      const { signOut } = await import('aws-amplify/auth');
      await signOut();
      onLoginSuccess(null);
      
      // Redirect to sign out URL if specified
      if (process.env.REACT_APP_REDIRECT_SIGN_OUT) {
        window.location.href = process.env.REACT_APP_REDIRECT_SIGN_OUT;
      }
      
      toast.success('Signed out successfully');
      setDebugInfo({});
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
      setDebugInfo({
        error: error.message,
        stage: 'signout'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginCard}>
        <h2>Welcome to 4exAI</h2>
        <p>Sign in to access your trading strategies and analysis tools</p>
        
        <button
          className={styles.googleButton}
          onClick={handleGoogleSignIn}
          disabled={isLoading}
        >
          {isLoading ? (
            <span>Signing in...</span>
          ) : (
            <>
              <svg className={styles.googleIcon} viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default Login; 